import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Pencil, HardDrive, Download, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { devicesService } from '../../services/devices';
import type { Device } from '../../types/device';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { useGymAdmin } from '../context/gym-admin-context';

const SUPPORT_EMAIL = 'admin@divepeaks.com';

/**
 * The gym's proxy devices. The section stays in the sidebar even for gyms with
 * none, so the empty state is where they learn how to ask for one — there is no
 * self-service path to a piece of hardware.
 */
export default function DevicesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();
  const { gym, loading: gymLoading, slug } = useCurrentGym();

  const [devices, setDevices] = useState<Device[]>([]);
  const [listLoading, setListLoading] = useState(true);

  const loadDevices = useCallback(
    async (gymSlug: string) => {
      setListLoading(true);
      try {
        setDevices(await devicesService.list(gymSlug));
      } catch (error) {
        console.error('Failed to load devices:', error);
        toast({ title: t('gymAdmin.devices.loadError'), variant: 'destructive' });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) loadDevices(slug);
  }, [slug, loadDevices]);

  if (gymLoading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!gym || !slug) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <p className="text-sm text-muted-foreground">{t('gymAdmin.devices.subtitle')}</p>

      {listLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : devices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <HardDrive className="mx-auto mb-3 size-8 opacity-50" />
            <p>
              {/* The children are the fallback; the translated string wins
                  whenever the key resolves, with <mail> mapped to the link. */}
              <Trans
                i18nKey="gymAdmin.devices.empty"
                components={{
                  mail: <a className="underline" href={`mailto:${SUPPORT_EMAIL}`} />,
                }}
              >
                No device linked yet — request one at{' '}
                <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
              </Trans>
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {devices.map((device) => (
            <Card key={device.id}>
              <CardContent className="flex flex-wrap items-start gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate font-medium">{device.name}</h3>
                    <Badge variant="outline" className="font-mono">
                      {device.code}
                    </Badge>
                    {/* The version the box reports, so the running firmware is
                        readable without opening the device. */}
                    {device.firmware?.version && (
                      <Badge variant="secondary" className="font-mono">
                        v{device.firmware.version}
                      </Badge>
                    )}
                    {/* Only the two states that ask for a decision are flagged
                        here; the rest is on the device's own page. */}
                    {device.firmware?.status === 'update_available' && (
                      <Badge>
                        <Download className="mr-1 size-3" />
                        {t('gymAdmin.devices.firmware.status.update_available')}
                      </Badge>
                    )}
                    {device.firmware?.status === 'updating' && (
                      <Badge>
                        <Loader2 className="mr-1 size-3 animate-spin" />
                        {t('gymAdmin.devices.firmware.status.updating')}
                      </Badge>
                    )}
                    {device.firmware?.status === 'update_failed' && (
                      <Badge variant="destructive">
                        <AlertTriangle className="mr-1 size-3" />
                        {t('gymAdmin.devices.firmware.status.update_failed')}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('gymAdmin.devices.boardCount', { count: device.boards.length })}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(placePath(`/devices/${device.id}/edit`))}
                  aria-label={t('common.edit')}
                >
                  <Pencil className="size-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
